<template>
  <div>
    <Head :title="__(title)" />
    <div class="mb-6 flex justify-between items-center">
      <search-input v-model="searchForm.search" class="w-full max-w-md mr-4" @reset="reset"></search-input>
      <Link
        v-if="user_access.outgoing_asset.create"
        class="btn-indigo"
        :href="this.route('exit_goods.create')"
      >
        <span>{{ __('Create a New Data') }}</span>
      </Link>
    </div>
    <div class="bg-white rounded-md shadow overflow-x-auto">
      <table class="w-full whitespace-nowrap">
        <tr class="text-left font-bold">
          <th class="px-6 pt-6 pb-4">#{{ __('ID') }}</th>
          <th class="px-6 pt-6 pb-4">{{ __('Transaction Code') }}</th>
          <th class="px-6 pt-6 pb-4">{{ __('Customer') }}</th>
          <th class="px-6 pt-6 pb-4">{{ __('Users') }}</th>
          <th class="px-6 pt-6 pb-4">{{ __('Type Outgoing') }}</th>
          <th class="px-6 pt-6 pb-4">{{ __('Date') }}</th>
          <th class="px-6 pt-6 pb-4">{{ __('Action') }}</th>
        </tr>
        <tr
          v-for="(item, index) in data_exit_goods.data"
          :key="item.id"
          class="hover:bg-gray-100 focus-within:bg-gray-100"
        >
          <td class="border-t">
            <Link
              class="px-6 py-4 flex items-center focus:text-indigo-500"
              :href="route('exit_goods.show', item.id)"
            >{{ item.id }}</Link>
          </td>
          <td class="border-t">
            <Link
              class="px-6 py-4 flex items-center focus:text-indigo-500"
              :href="route('exit_goods.show', item.id)"
            >{{ item.transaction_code }}</Link>
          </td>
          <td class="border-t">
            <Link
              class="px-6 py-4 flex items-center focus:text-indigo-500"
              :href="route('exit_goods.show', item.id)"
            >{{ item.customer_id }}</Link>
          </td>
          <td class="border-t">
            <Link
              class="px-6 py-4 flex items-center focus:text-indigo-500"
              :href="route('exit_goods.show', item.id)"
            >{{ item.organization_id }}</Link>
          </td>
          <td class="border-t">
            <Link
              class="px-6 py-4 flex items-center focus:text-indigo-500"
              :href="route('exit_goods.show', item.id)"
            >
              <span
                :class="['px-2 py-1 rounded-full font-bold', item.type_outgoing === 'sale' ? 'bg-red-500 text-white' : item.type_outgoing === 'leases' ? 'bg-yellow-500 text-white' : 'bg-gray-200 text-black']"
              >{{ item.type_outgoing === 'sale' ? 'Sales' : item.type_outgoing === 'leases' ? 'Leases' : 'Unknown' }}</span>
            </Link>
          </td>
          <td class="border-t">
            <Link
              class="px-6 py-4 flex items-center focus:text-indigo-500"
              :href="route('exit_goods.edit', item.id)"
            >{{ formatDate(item.created_at, 'YYYY-MM-DD') }}</Link>
          </td>
          <td class="border-t w-px">
            <div class="flex" @click.stop="toggleDropdown(item.id)">
              <button class="px-4 flex items-center justify-start space-x-4" tabindex="-1">
                <icon name="dots-vertical" class="block w-5 h-5" />
              </button>
              <div
                v-if="dropdownOpen[item.id]"
                class="z-50 mt-2 right-0 w-48 bg-white rounded-md shadow-lg absolute"
              >
                <Link
                  v-if="user_access.outgoing_asset.update"
                  :href="this.route('exit_goods.edit', item.id)"
                  class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >Edit</Link>
                <Link
                  v-if="user_access.outgoing_asset.read"
                  :href="this.route('exit_goods.show', item.id)"
                  class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >Detail</Link>
                <button
                  v-if="user_access.outgoing_asset.delete"
                  @click="destroy(item.id)"
                  class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >Delete</button>
                <Link
                  v-if="user_access.outgoing_asset.update"
                  :href="this.route('exit_goods.view_bast', item.id)"
                  class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >Upload BTTPU & BAST</Link>
                <a
                  :href="`/dashboard/asset_management/exit_goods/export_pdf/${item.id}`"
                  class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >Generate BTTPU</a>
              </div>
            </div>
          </td>
        </tr>
        <tr v-if="data_exit_goods.data.length === 0">
          <td class="border-t px-6 py-4" colspan="7">No data found.</td>
        </tr>
      </table>
    </div>
    <pagination class="mt-6" :links="data_exit_goods.links" />
  </div>
</template>

  <script>
import { Link, Head } from '@inertiajs/vue3'
import Icon from '@/Shared/Icon'
import pickBy from 'lodash/pickBy'
import Layout from '@/Shared/Layout'
import throttle from 'lodash/throttle'
import Pagination from '@/Shared/Pagination'
import SearchInput from '@/Shared/SearchInput'
import { formatDate } from '@/utils/dateFormatter.js'

export default {
  metaInfo: { title: 'Outgoing Goods' },
  components: {
    Icon,
    Link,
    Head,
    Pagination,
    SearchInput,
  },
  layout: Layout,
  props: {
    title: String,
    filters: Object,
    data_exit_goods: Object,
  },
  data() {
    return {
      searchForm: this.$inertia.form({
        search: this.filters.search,
      }),
      uploadForm: this.$inertia.form({
        bast_image: null, // File input for BAST image
      }),
      dropdownOpen: {},
      showUploadModal: false, // Modal visibility control
      selectedItem: null, // Track selected ExitGoods item for uploading BAST
      previewUrl: null, // URL for image preview
      user_access: this.$page.props.auth.user.access,
    }
  },
  watch: {
    searchForm: {
      deep: true,
      handler: throttle(function () {
        this.$inertia.get(this.route('exit_goods'), pickBy(this.searchForm), { preserveState: true })
      }, 150),
    },
  },
  mounted() {
    document.addEventListener('click', this.closeAllDropdowns)
  },
  beforeUnmount() {
    document.removeEventListener('click', this.closeAllDropdowns)
  },
  methods: {
    toggleDropdown(id) {
      this.dropdownOpen = { ...this.dropdownOpen, [id]: !this.dropdownOpen[id] }
    },
    closeAllDropdowns() {
      this.dropdownOpen = {}
    },
    reset() {
      this.searchForm.search = null
    },
    destroy(id) {
      if (confirm('Are you sure you want to delete this data?')) {
        this.$inertia.delete(this.route('exit_goods.destroy', id))
      }
    },
    formatDate(dateString, format) {
      return formatDate(dateString, format)
    },
    closeUploadModal() {
      this.showUploadModal = false
    },
    submitUploadForm() {
      console.log('Form submitted') // Ensure method is called
      this.uploadForm.post(this.route('exit_goods.upload_bast', this.selectedItem.id), {
        onSuccess: () => {
          console.log('Upload successful')
          this.closeUploadModal()
        },
        onError: (errors) => {
          console.error('Upload failed', errors)
        },
      })
    },
  },
}
</script>
